import { DownOutlined, PlusOutlined } from '@ant-design/icons';
import AppButton from '@nxsys/components/AppButton';
import AppCard from '@nxsys/components/AppCard';
import AppPageTitle from '@nxsys/components/AppPageTitle';
import AppsContainer from '@nxsys/components/AppsContainer';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppsHeader from '@nxsys/components/AppsContainer/AppsHeader';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import { InvoiceTypeEnum } from '@nxsys/constants/AppEnums';
import { useThemeContext } from '@nxsys/context/AppContextProvider/ThemeContextProvider';
import {
  useDownloadAllCreditNote,
  useDownloadCreditNote,
  useGetCreditNoteList,
  usePreviewCreditNote,
  useSendInvoice,
} from '@nxsys/hooks/api/invoice/useInvoiceApi';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { GetInvoiceListParams } from '@nxsys/types/api';
import { Invoice } from '@nxsys/types/models/invoice';
import { showCurrencyByCountry } from '@nxsys/utils';
import { csvConfigExport } from '@nxsys/utils/export';
import { showMessage } from '@nxsys/utils/message';
import { formatMoney } from '@nxsys/utils/number-helper';
import { Dropdown } from 'antd';
import { ExportToCsv } from 'export-to-csv';
import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { ActionsHeader } from '../components/ActionsHeader';
import { getColumns } from './column';
import { useNavigateWithQuery } from '@nxsys/hooks/useNavigateWithQuery';
import useNotificationSocket from '@nxsys/hooks/useNotificationSocket';

const DEFAULT_PAGE_SIZE = 20;

const CreditNoteListing = () => {
  const { messages } = useTypedIntl();
  const { theme } = useThemeContext();
  const navigate = useNavigateWithQuery();

  const { user, isBureau, isBureauEmployer, isAgency, payrollCountry } =
    useSelector((state: AppState) => state.appStore);

  const { value, loading, run, refetch } = useGetCreditNoteList();
  const invoiceList = value?.data ?? [];
  const lastArgs = value?.lastArgs ?? {};

  const { run: previewCreditNoteRun, loading: isPreviewCreditNotePending } =
    usePreviewCreditNote();

  const { run: sendInvoiceRun, loading: isSendInvoicePending } =
    useSendInvoice();

  const { run: downloadCreditNoteRun, loading: isDownloadCreditNotePending } =
    useDownloadCreditNote();

  const {
    run: downloadAllCreditNoteRun,
    loading: isDownloadAllCreditNotePending,
  } = useDownloadAllCreditNote();

  const [selectedRows, setSelectedRows] = useState<Invoice[]>([]);

  useEffect(() => {
    run({
      page: 0,
      size: DEFAULT_PAGE_SIZE,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useNotificationSocket({
    eventName: `send-mail-invoice-${user?.id}`,
    onSuccess: () => {
      showMessage({
        type: 'success',
        content: messages['common.sendMailSuccess'],
      });
      refetch();
    },
    onError: () => {
      showMessage({
        type: 'error',
        content: messages['common.sendMailFailed'],
      });
    },
  });

  const handleChangePagination = (page: number, pageSize: number) => {
    run({
      ...lastArgs,
      page: page - 1,
      size: pageSize,
    });
  };

  const renderTitle = useCallback(() => {
    return (
      <AppPageTitle
        prefix={messages['credit_note.title']}
        separator=""
        title={''}
      />
    );
  }, [messages]);

  const getMenuItems = () => {
    if (isAgency || isBureauEmployer) {
      return [
        {
          key: 'export',
          label: messages['invoices.export'],
          onClick: handleExportSelectedInvoice,
        },
        {
          key: 'exportAll',
          label: messages['invoices.exportAll'],
          onClick: handleExportAllInvoice,
        },
        {
          key: 'download',
          label: messages['invoices.download'],
          onClick: handleDownloadSelectedInvoice,
        },
        {
          key: 'downloadAll',
          label: messages['invoices.downloadAll'],
          onClick: handleDownloadAllInvoice,
        },
      ];
    }

    return [
      {
        key: 'emailCreditNote',
        label: messages['invoices.emailCreditNote'],
        onClick: handleSendInvoiceSelected,
      },
      {
        key: 'export',
        label: messages['invoices.export'],
        onClick: handleExportSelectedInvoice,
      },
      {
        key: 'exportAll',
        label: messages['invoices.exportAll'],
        onClick: handleExportAllInvoice,
      },
      {
        key: 'download',
        label: messages['invoices.download'],
        onClick: handleDownloadSelectedInvoice,
      },
      {
        key: 'downloadAll',
        label: messages['invoices.downloadAll'],
        onClick: handleDownloadAllInvoice,
      },
    ];
  };

  const renderRightContent = () => {
    const menuProps = {
      items: getMenuItems(),
    };

    return (
      <div className="flex items-center gap-2">
        {!isAgency && !isBureauEmployer && (
          <AppButton
            type="primary"
            name={messages['invoices.addNew']}
            onClick={() => navigate('/invoices/create-invoice-credit')}
            leftIcon={<PlusOutlined />}
          />
        )}
        <Dropdown menu={menuProps}>
          <AppButton
            type="primary"
            name={messages['common.action']}
            rightIcon={<DownOutlined />}
          />
        </Dropdown>
      </div>
    );
  };

  const handleSearch = (value: string) => {
    run({
      ...lastArgs,
      page: 0,
      search: value,
    });
  };

  const handleFilter = (params: GetInvoiceListParams) => {
    run({
      page: 0,
      size: lastArgs.size,
      search: lastArgs.search,
      ...params,
    });
  };

  const handlePreviewInvoice = async (id: string) => {
    if (!id) return;
    try {
      const res = await previewCreditNoteRun({ id });
      if (res?.data) {
        window.open(res.data, '_blank');
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleSendInvoice = async (ids: string[]) => {
    try {
      await sendInvoiceRun({
        ids,
        templateKey: 'credit_note',
      });
    } catch (error) {
      console.error(error);
    }
  };

  const handleSendInvoiceSelected = () => {
    if (selectedRows.length === 0) {
      showMessage({
        type: 'warning',
        content: messages['common.noSelected'],
      });
      return;
    }
    handleSendInvoice(selectedRows.map((row) => row.id));
  };

  const handleExportInvoice = (data: Invoice[]) => {
    if (!data?.length) {
      return;
    }
    const dataExport = data.map((item: any) => ({
      creditNo: isBureau ? item?.bureauCreditNo : item?.creditNo || '',
      creditDate: item?.creditDate
        ? moment(item?.creditDate).tz('Europe/London').format('DD/MM/YYYY')
        : '',
      from: item?.companyRef?.companyName || '',
      to: item?.agencyRef?.name || '',
      totalNet: formatMoney(item?.totalNet || 0, showCurrencyByCountry(payrollCountry)),
      totalVat: formatMoney(item?.totalVat || 0, showCurrencyByCountry(payrollCountry)),
      totalPrices: formatMoney(item?.totalPrices || 0, showCurrencyByCountry(payrollCountry)),
      receipt: formatMoney(item?.receipt || 0, showCurrencyByCountry(payrollCountry)),
      outstanding: formatMoney(item?.outstanding || 0, showCurrencyByCountry(payrollCountry)),
    }));

    const headers = [
      'CRN No',
      'Date',
      'From',
      'To',
      'Net',
      'VAT',
      'Total',
      'Paid',
      'Outstanding',
    ];

    const exportName = csvConfigExport(`Credit-export`, headers);
    const csvExporter = new ExportToCsv(exportName);
    csvExporter.generateCsv(dataExport);
  };

  const handleExportSelectedInvoice = () => {
    if (selectedRows.length === 0) {
      showMessage({
        type: 'warning',
        content: messages['common.noSelected'],
      });
      return;
    }
    handleExportInvoice(selectedRows);
  };

  const [isExportingAll, setIsExportingAll] = useState(false);

  const handleExportAllInvoice = async () => {
    if (invoiceList.length === 0) {
      showMessage({
        type: 'warning',
        content: messages['invoices.empty'],
      });
      return;
    }
    try {
      setIsExportingAll(true);
      const MAX_EXPORT_SIZE = 10000;
      const res = await run({
        page: 0,
        size: Math.min(value?.totalDocs || 0, MAX_EXPORT_SIZE),
        isNexus: true,
      });
      handleExportInvoice(res.data);
    } catch (error) {
      console.error(error);
    } finally {
      setIsExportingAll(false);
    }
  };

  const handleDownloadSelectedInvoice = async () => {
    if (selectedRows.length === 0) {
      showMessage({
        type: 'warning',
        content: messages['common.noSelected'],
      });
      return;
    }
    try {
      const res = await downloadCreditNoteRun({
        ids: selectedRows.map((row) => row.id),
      });
      if (res?.data) {
        window.open(res.data, '_blank');
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleDownloadAllInvoice = async () => {
    try {
      const res = await downloadAllCreditNoteRun();
      if (res.success) {
        showMessage({
          type: 'success',
          content: messages['invoices.downloadAllSuccess'],
        });
      } else {
        showMessage({
          type: 'error',
          content: messages['invoices.downloadAllFailed'],
        });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleViewCreditNoteDetail = (id: string) => {
    navigate(`/invoices/credit-note/${id}`);
  };

  const isFiltering =
    !!lastArgs?.search ||
    !!lastArgs?.isOutstanding ||
    !!lastArgs?.isPaid ||
    !!lastArgs?.startDate ||
    !!lastArgs?.endDate ||
    !!lastArgs?.invoiceNo ||
    !!lastArgs?.creditNo ||
    !!lastArgs?.totalPrices;

  return (
    <AppsContainer
      fullView
      type="bottom"
      title={renderTitle()}
      rightContent={renderRightContent()}
      loading={
        isPreviewCreditNotePending ||
        isSendInvoicePending ||
        isDownloadCreditNotePending ||
        isDownloadAllCreditNotePending ||
        isExportingAll
      }
    >
      <AppCard>
        <AppsHeader>
          <ActionsHeader
            onSearch={handleSearch}
            onFilter={handleFilter}
            invoiceType={InvoiceTypeEnum.CREDIT_NOTE}
          />
        </AppsHeader>
        <AppsContent>
          <AppTableContainer
            rowKey="id"
            loading={loading}
            data={invoiceList ?? []}
            page={lastArgs?.page ?? 0}
            pageSize={lastArgs?.size ?? DEFAULT_PAGE_SIZE}
            total={value?.totalDocs ?? 0}
            titleEmpty={messages['creditNote.empty']}
            columns={getColumns({
              messages,
              theme,
              handlePreviewInvoice,
              handleSendInvoice,
              invoiceType: InvoiceTypeEnum.CREDIT_NOTE,
              isBureau,
              isBureauEmployer,
              role: user?.role ?? '',
              handleViewCreditNoteDetail,
              payrollCountry,
            })}
            onChangePagination={handleChangePagination}
            rowSelection={{
              type: 'checkbox',
              onChange: (_: React.Key[], selectedRows: any[]) => {
                setSelectedRows(selectedRows);
              },
            }}
            noDataType={isFiltering ? 'noDataFound' : (isAgency || isBureauEmployer) ? 'noDataToShow' : 'noDataCreated'}
            addNewBtnOnClick={(isAgency || isBureauEmployer) ? undefined : () => navigate('/invoices/create-invoice-credit')}
            dataTypeName={messages['common.creditNote']}
          />
        </AppsContent>
      </AppCard>
    </AppsContainer>
  );
};

export default CreditNoteListing;
